import React, {useCallback, useEffect} from "react";
import {Tabs, Tab, Stack, Paper, Box, Typography, Chip, Tooltip} from "@mui/material";
import useSWR from "swr";
import {getFetcher, putFetcher} from "../../utils/api/fetchers";
import {Season} from "../../schemas/seasons/seasonsSchema";
import {Race} from "../../schemas/races/racesSchema";
import {CategoryShort} from "../../schemas/category/categoriesSchema";
import EntityTable, {EntityTableHeaderProps} from "../../components/list/EntityList";
import {RaceOverallResults} from "../../schemas/results/race/RaceOverallResults";
import {RaceCategoryResults} from "../../schemas/results/race/RaceCategoryResults";
import {SeasonOverallResults} from "../../schemas/results/season/SeasonOverallResults";
import {SeasonCategoryResults} from "../../schemas/results/season/SeasonCategoryResults";
import AccordionRowCategory from "./AccordionRowCategory";
import AccordionRowOverall from "./AccordionRowOverall";
import {useNotification} from "../../utils/hooks/useNotification";
import {DropResult} from "@hello-pangea/dnd";
import {Club} from "../../schemas/club/clubSchema"; // viz dále
import useTableParams from "../../components/list/useTableParams";
import {getCategoryColor} from "../../utils/categories/categoryColors";

interface ResultsTableViewProps {
    season: Season | null,
    race: Race | null,
    resultType: "season" | "race",
    selectedTab: number,
    editMode: boolean,
    tabs: { key: number, label: string }[],
    onTabChange: (tab: number) => void,
    categories: CategoryShort[],
    races: Race[],
    isMobile?: boolean,
    onMutateReady?: (mutateFunctions: {
        mutateRaceOverall: () => Promise<any>,
        mutateRaceCategory: () => Promise<any>,
        mutateSeasonOverall: () => Promise<any>,
        mutateSeasonCategory: () => Promise<any>,
    }) => void;
}

const ResultsTableView: React.FC<ResultsTableViewProps> = ({
                                                               season,
                                                               race,
                                                               resultType,
                                                               selectedTab,
                                                               editMode,
                                                               tabs,
                                                               onTabChange,
                                                               categories,
                                                               races,
                                                               isMobile,
                                                               onMutateReady
                                                           }) => {
    const {showNotification} = useNotification();

    const {data: clubs} = useSWR<Club[]>("/clubs/all", getFetcher);
    const {
        data: raceOverallResults,
        mutate: mutateRaceOverall
    } = useSWR<RaceOverallResults[]>(race ? `/races/${race.id}/results/overall` : null, getFetcher);
    const {
        data: raceCategoryResults,
        mutate: mutateRaceCategory
    } = useSWR<RaceCategoryResults[]>((race && selectedTab > 0) ? `/races/${race.id}/results/categories/${categories.find(category => category.categoryNumber === selectedTab)?.id}` : null, getFetcher);
    const {
        data: seasonOverallResults,
        mutate: mutateSeasonOverall
    } = useSWR<SeasonOverallResults[]>(season ? `/seasons/${season.id}/results/overall` : null, getFetcher);
    const {
        data: seasonCategoryResults,
        mutate: mutateSeasonCategory
    } = useSWR<SeasonCategoryResults[]>((season && selectedTab > 0) ? `/seasons/${season.id}/results/categories/${categories.find(category => category.categoryNumber === selectedTab)?.id}` : null, getFetcher);

    useEffect(() => {
        if (onMutateReady) {
            onMutateReady({
                mutateRaceOverall,
                mutateRaceCategory,
                mutateSeasonOverall,
                mutateSeasonCategory
            });
        }
    }, [mutateRaceCategory, mutateRaceOverall, mutateSeasonCategory, mutateSeasonOverall, onMutateReady]);

    const seasonOverallTableId = "seasonOverallResults";
    const seasonCategoryTableId = "seasonCategoryResults";
    const raceOverallTableId = "raceOverallResults";
    const raceCategoryTableId = "raceCategoryResults";

    const seasonOverallParams = useTableParams(seasonOverallTableId);
    const seasonCategoryParams = useTableParams(seasonCategoryTableId + selectedTab);
    const raceOverallParams = useTableParams(raceOverallTableId);
    const raceCategoryParams = useTableParams(raceCategoryTableId + selectedTab);

    const handleRaceResultsDragEnd = useCallback(async (result: DropResult) => {
        if (!result.destination || result.destination.index === result.source.index) return;

        const isOverall = selectedTab === 0;
        const results = isOverall ? (raceOverallResults ?? []) : (raceCategoryResults ?? []);
        if (!race) return;

        const movedRow = results[result.source.index];
        if (movedRow?.boat?.disqualified) {
            showNotification("Pořadí diskvalifikované lodi nelze měnit.", "warning");
            return;
        }

        const validBoatsCount = results.filter(r => !r.boat?.disqualified).length;
        if (result.destination.index >= validBoatsCount) {
            showNotification("Platnou loď nelze přesunout mezi diskvalifikované lodě.", "warning");
            return;
        }

        results.splice(result.source.index, 1);
        results.splice(result.destination.index, 0, movedRow);

        const payload = {
            position: result.destination.index + 1,
            boatId: movedRow.boat.boatId,
        };

        const endpoint = isOverall
            ? `/races/${race.id}/results/overall`
            : `/races/${race.id}/results/categories/${categories.find(cat => cat.categoryNumber === selectedTab)?.id}`;

        try {
            await putFetcher(endpoint, payload);
            showNotification("Pořadí bylo úspěšně změněno.", "success");
            if (isOverall) {
                mutateRaceOverall();
            } else {
                mutateRaceCategory();
            }
        } catch (e) {
            showNotification("Nepodařilo se uložit nové pořadí.", "error");
            console.error(e);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [race, selectedTab, raceOverallResults, raceCategoryResults, categories]);

    const applyFilters = (entities: any[], headers: EntityTableHeaderProps[], filters: Record<string, string>): any[] => {
        if (!entities || Object.keys(filters).length === 0) return entities;

        console.log("Applying filters:", filters);
        return entities.filter(entity => {
            return headers.every(header => {
                if (!header.filtered) return true;
                const filterValue = filters[header.key];
                if (!filterValue) return true;

                // multiselect filtering
                if (header.filterConfig?.type === "multiSelect") {
                    const options = filterValue.split(",");
                    const entityValue = getNestedValue(entity, header.key);
                    return options.includes(entityValue?.toString());
                }

                // case-insensitive string filtering
                const entityValue = getNestedValue(entity, header.key);
                return entityValue?.toString().toLowerCase().includes(filterValue.toLowerCase());
            });
        });
    }

    const getNestedValue = (obj: any, path: string): any => {
        return path.split('.').reduce((acc, part) => acc?.[part], obj);
    }

    const seasonOverallResultsHeaders: EntityTableHeaderProps[] = [
        {
            key: "position", label: "Pořadí", render(position: number) {
                return <Typography>{position}.</Typography>;
            }
        },
        {key: "boat.name", label: "Jméno lodi", filtered: true},
        {
            key: "boat.category.name", label: "Kategorie", filtered: true, filterConfig: {
                type: 'multiSelect',
                options: [
                    ...(categories || []).map(category => ({
                        label: category.name,
                        value: category.name
                    }))
                ]
            },
            render: (categoryName, boat) => (
                <span
                    style={{color: getCategoryColor(boat?.boat?.category?.categoryNumber), fontWeight: "bold"}}
                >
                    {categoryName}
                </span>
            )
        },
        {
            key: "boat.club.name", label: "Klub", filtered: true, filterConfig: {
                type: 'multiSelect',
                options: [
                    ...(clubs || []).map((club: Club) => ({
                        label: club.name,
                        value: club.name
                    }))
                ]
            }
        },
        {key: "points", label: "Body celkem"},
        {key: "racesCount", label: "Počet odjetých závodů"},
    ];
    const seasonCategoryResultsHeaders: EntityTableHeaderProps[] = [
        {
            key: "position", label: "Pořadí", render(position: number) {
                return <Typography>{position}.</Typography>;
            }
        },
        {key: "boat.name", label: "Jméno lodi", filtered: true},
        {
            key: "boat.club.name", label: "Klub", filtered: true, filterConfig: {
                type: 'multiSelect',
                options: [
                    ...(clubs || []).map((club: Club) => ({
                        label: club.name,
                        value: club.name,
                    }))
                ]
            }
        },
        {key: "points", label: "Body celkem"},
        {key: "racesCount", label: "Počet odjetých závodů"},
    ];
    const raceOverallResultsHeaders: EntityTableHeaderProps[] = [
        {
            key: "position", label: "Pořadí", render(position: number, row: any) {
                if (row?.boat?.disqualified) {
                    return (
                        <Tooltip title={row?.boat?.notes ? `Důvod diskvalifikace: ${row.boat.notes}` : "Loď byla diskvalifikována"} arrow>
                            <Chip label="DSQ" color="error" size="small" sx={{fontWeight: "bold"}} />
                        </Tooltip>
                    );
                }
                return <Typography>{position}.</Typography>;
            }
        },
        {key: "boat.name", label: "Jméno lodi", filtered: true},
        {
            key: "boat.category.name", label: "Kategorie", filtered: true, filterConfig: {
                type: 'multiSelect',
                options: [
                    ...(categories || []).map(category => ({
                        label: category.name,
                        value: category.name
                    }))
                ]
            },
            render: (categoryName, boat) => (
                <span
                    style={{color: getCategoryColor(boat?.boat?.category?.categoryNumber), fontWeight: "bold"}}
                >
                    {categoryName}
                </span>
            )
        },
        {
            key: "boat.club.name", label: "Klub", filtered: true, filterConfig: {
                type: 'multiSelect',
                options: [
                    ...(clubs || []).map((club: Club) => ({
                        label: club.name,
                        value: club.name
                    }))
                ]
            }
        },
        {key: "points", label: "Body"},
    ];
    const raceCategoryResultsHeaders: EntityTableHeaderProps[] = [
        {
            key: "position", label: "Pořadí", render(position: number, row: any) {
                if (row?.boat?.disqualified) {
                    return (
                        <Tooltip title={row?.boat?.notes ? `Důvod diskvalifikace: ${row.boat.notes}` : "Loď byla diskvalifikována"} arrow>
                            <Chip label="DSQ" color="error" size="small" sx={{fontWeight: "bold"}} />
                        </Tooltip>
                    );
                }
                return <Typography>{position}.</Typography>;
            }
        },
        {key: "boat.name", label: "Jméno lodi", filtered: true},
        {
            key: "boat.club.name", label: "Klub", filtered: true, filterConfig: {
                type: 'multiSelect',
                options: [
                    ...(clubs || []).map((club: Club) => ({
                        label: club.name,
                        value: club.name
                    }))
                ]
            }
        },
        {key: "points", label: "Body"},
    ]

    const filteredSeasonOverall = applyFilters(seasonOverallResults ?? [], seasonOverallResultsHeaders, seasonOverallParams.filters);
    const filteredSeasonCategory = applyFilters(seasonCategoryResults ?? [], seasonCategoryResultsHeaders, seasonCategoryParams.filters);
    const filteredRaceOverall = applyFilters(raceOverallResults ?? [], raceOverallResultsHeaders, raceOverallParams.filters);
    const filteredRaceCategory = applyFilters(raceCategoryResults ?? [], raceCategoryResultsHeaders, raceCategoryParams.filters);

    return (
        <Stack direction="column" spacing={1.5} sx={{width: "100%"}}>
            {!isMobile && (
                <Tabs value={selectedTab} onChange={(_, v) => onTabChange(v)}
                      scrollButtons="auto" variant="scrollable"
                      sx={{
                          borderBottom: "1px solid #ddd",
                      }}
                >
                    {
                        tabs.map(tab => (
                                <Tab key={tab.key} label={tab.label} value={tab.key}
                                     sx={{
                                         "&.Mui-selected": {
                                             color: tab.key === 0 ? "black" : getCategoryColor(tab.key),
                                             backgroundColor: "rgba(0, 0, 0, 0.05)"
                                         },
                                         color: tab.key === 0 ? "black" : getCategoryColor(tab.key),
                                         fontWeight: "bold"
                                     }}/>
                            )
                        )
                    }
                </Tabs>
            )
            }
            <Paper elevation={isMobile ? 0 : 1} sx={{p: 2, pt: 0, px: isMobile ? 0 : 2}}>
                {resultType === "season" && selectedTab === 0 && (
                    <EntityTable
                        tableId={seasonOverallTableId}
                        headers={seasonOverallResultsHeaders}
                        entities={filteredSeasonOverall}
                        entitiesCount={filteredRaceOverall.length}
                        showLoading={false}
                        paged={false}
                        renderAccordion={(row: SeasonOverallResults) => <AccordionRowOverall row={row} races={races}/>}
                        isMobile={isMobile}
                    />
                )
                }
                {
                    resultType === "season" && selectedTab !== 0 && (
                        <EntityTable
                            key={selectedTab}
                            tableId={seasonCategoryTableId + selectedTab}
                            headers={seasonCategoryResultsHeaders}
                            entities={filteredSeasonCategory}
                            entitiesCount={filteredSeasonCategory.length}
                            showLoading={false}
                            paged={false}
                            renderAccordion={(row: SeasonCategoryResults) => <AccordionRowCategory row={row}
                                                                                                   races={races}/>}
                            isMobile={isMobile}
                        />
                    )
                }
                {
                    resultType === "race" && race && selectedTab === 0 && (
                        <EntityTable
                            tableId={raceOverallTableId}
                            headers={raceOverallResultsHeaders}
                            entities={filteredRaceOverall}
                            entitiesCount={filteredRaceOverall.length}
                            showLoading={false}
                            draggable={editMode}
                            onDragEnd={editMode ? handleRaceResultsDragEnd : undefined}
                            paged={false}
                            isMobile={isMobile}
                        />
                    )
                }{
                resultType === "race" && race && selectedTab !== 0 && (
                    <EntityTable
                        key={selectedTab}
                        tableId={raceCategoryTableId + selectedTab}
                        headers={raceCategoryResultsHeaders}
                        entities={filteredRaceCategory}
                        entitiesCount={filteredRaceCategory.length}
                        showLoading={false}
                        draggable={editMode}
                        onDragEnd={editMode ? handleRaceResultsDragEnd : undefined}
                        paged={false}
                        isMobile={isMobile}
                    />
                )
            }{
                resultType === "race" && !race && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "70vh",
                        }}
                    >
                        <Typography variant="h6" color="text.secondary">
                            Není vybrán žádný závod
                        </Typography>
                    </Box>
                )
            }
            </Paper>
        </Stack>
    )
        ;
}

export default ResultsTableView;